import type { ElplanAlertMessage } from 'api/elplan/elplan.alert.types'
import { isBidSpanCellFault } from 'api/elplan/elplan.cellFault.helper'
import type { ElplanDayTableCellFault } from 'api/elplan/elplan.cellFault.types'
import {
  DAY_AHEAD_BID_NAME_WITHOUT_VOLUME_ALERT,
  DAY_AHEAD_BID_PRICE_WITHOUT_VOLUME_ALERT,
  resolveDayAheadBidValueFaultAlert,
  resolveElplanCellFaultAlert,
} from 'api/elplan/elplan.cellFaultAlert.helper'
import { readElplanDayTableSaveError } from 'api/elplan/elplan.dayTableSaveError.helper'
import { findDayAheadBidContentWithoutVolume, holdsDayAheadBidContent } from 'helpers/elplan.helper/bid.helper'
import { isSameDayAheadBidValue, readDayAheadBidValue } from 'helpers/elplan.helper/bidValue.helper'
import type { RowValues } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import { getDayAheadBidCellKey, makeDayAheadBidHourRowLookup } from './DayAheadBidTable.grid.helper'
import type {
  DayAheadBidCellFault,
  DayAheadBidColumn,
  DayAheadBidRow,
  DayAheadBidSaveRejection,
} from './DayAheadBidTable.types'

const WITHOUT_VOLUME_ALERT_BY_CONTENT_CELL = {
  name: DAY_AHEAD_BID_NAME_WITHOUT_VOLUME_ALERT,
  price: DAY_AHEAD_BID_PRICE_WITHOUT_VOLUME_ALERT,
} satisfies Record<'name' | 'price', ElplanAlertMessage>

type FindDayAheadBidLocalCellFaultsInput = {
  values: RowValues[]
  columns: DayAheadBidColumn[]
  rows: DayAheadBidRow[]
}

export function toDayAheadBidCellFaults(
  faults: ElplanDayTableCellFault[],
  rows: DayAheadBidRow[]
): DayAheadBidCellFault[] {
  const priceRowIndex = rows.findIndex((row) => row.kind === 'price')
  const priceFaultRowIndex = priceRowIndex < 0 ? undefined : priceRowIndex
  const findHourRowIndex = makeDayAheadBidHourRowLookup(rows)

  return faults.map((fault) => ({
    tagId: fault.tag,
    rowIndex: fault.startTime === null ? priceFaultRowIndex : findHourRowIndex(fault.startTime),
    alert: resolveElplanCellFaultAlert(fault),
    isColumnWide: isBidSpanCellFault(fault),
    clearedWhenAnchor: 'changes',
  }))
}

export function toDayAheadBidSaveRejection(
  error: unknown,
  rejectedValues: RowValues[],
  rows: DayAheadBidRow[]
): DayAheadBidSaveRejection {
  const { cellFaults, requestAlert, errorCode } = readElplanDayTableSaveError(error)
  return { faults: toDayAheadBidCellFaults(cellFaults, rows), rejectedValues, requestAlert, errorCode }
}

export function findDayAheadBidLocalCellFaults({
  values,
  columns,
  rows,
}: FindDayAheadBidLocalCellFaultsInput): DayAheadBidCellFault[] {
  const nameRowIndex = rows.findIndex((row) => row.kind === 'name')
  const priceRowIndex = rows.findIndex((row) => row.kind === 'price')
  const readCell = (rowIndex: number, tagId: number): string =>
    (values[rowIndex]?.[getDayAheadBidCellKey(rowIndex, tagId)] ?? '').trim()

  return columns.flatMap(({ tagId }) => {
    const valueFaults = rows.flatMap((row, rowIndex): DayAheadBidCellFault[] => {
      const reading = row.kind === 'name' ? undefined : readDayAheadBidValue(readCell(rowIndex, tagId))
      return reading?.kind === 'fault'
        ? [
            {
              tagId,
              rowIndex,
              alert: resolveDayAheadBidValueFaultAlert(reading.fault),
              isColumnWide: false,
              clearedWhenAnchor: 'changes',
            },
          ]
        : []
    })
    const hourVolumes = rows.flatMap((row, rowIndex) => (row.kind === 'hour' ? [readCell(rowIndex, tagId)] : []))
    const contentCell = findDayAheadBidContentWithoutVolume({
      name: nameRowIndex >= 0 ? readCell(nameRowIndex, tagId) : undefined,
      price: priceRowIndex >= 0 ? readCell(priceRowIndex, tagId) : undefined,
      hourVolumes,
    })
    if (contentCell === undefined) {
      return valueFaults
    }

    const withoutVolumeFault: DayAheadBidCellFault = {
      tagId,
      rowIndex: contentCell === 'name' ? nameRowIndex : priceRowIndex,
      alert: WITHOUT_VOLUME_ALERT_BY_CONTENT_CELL[contentCell],
      isColumnWide: true,
      clearedWhenAnchor: 'loses-bid-content',
    }
    return [...valueFaults, withoutVolumeFault]
  })
}

export function isDayAheadBidCellFaultStillApplicable(
  fault: DayAheadBidCellFault,
  rows: DayAheadBidRow[],
  values: RowValues[],
  rejectedValues: RowValues[]
): boolean {
  const isCellUnchanged = (checkRowIndex: number): boolean => {
    const cellKey = getDayAheadBidCellKey(checkRowIndex, fault.tagId)
    return isSameDayAheadBidValue(values[checkRowIndex]?.[cellKey], rejectedValues[checkRowIndex]?.[cellKey])
  }
  const isBidContentStillPresent = (anchorRowIndex: number): boolean => {
    const anchorKind = rows[anchorRowIndex]?.kind
    return (
      (anchorKind === 'name' || anchorKind === 'price') &&
      holdsDayAheadBidContent(anchorKind, values[anchorRowIndex]?.[getDayAheadBidCellKey(anchorRowIndex, fault.tagId)])
    )
  }
  const isAnchorCellUnresolved = (anchorRowIndex: number): boolean =>
    fault.clearedWhenAnchor === 'loses-bid-content'
      ? isBidContentStillPresent(anchorRowIndex)
      : isCellUnchanged(anchorRowIndex)

  if (fault.rowIndex !== undefined && !isAnchorCellUnresolved(fault.rowIndex)) {
    return false
  }

  return (
    !fault.isColumnWide || rows.every((row, checkRowIndex) => row.kind !== 'hour' || isCellUnchanged(checkRowIndex))
  )
}
